import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import { Dropdown, Icon, Message } from "semantic-ui-react";
import * as R from "ramda";
import {
  VictoryAxis,
  VictoryChart,
  VictoryGroup,
  VictoryLine,
  VictoryScatter,
  VictoryBoxPlot,
  VictoryTheme,
} from "victory";
import Sim from "./Sim";

const CUMULATIVE_DAYS_VALUES = [0, 5, 14, 20, 30, 60, 90, 120, 360];

@inject("store")
@observer
class SimsCharts extends Component {
  state = { config: {}, plotChartDays: this.props.sims[0].cumulativeDayStats.length > 14 ? 14 : 0 };

  getConfigCombinations = () => {
    const { sims } = this.props;
    return sims.reduce((configCombinations, sim) => {
      Object.entries(sim.config).forEach(([key, value]) => {
        if (key === "sellMakers" || key === "positiveFailSafe" || key === "rsi") {
          // TODO temporary ignore sellMakers
          // positiveFailSafe, rsi are expanded
          return configCombinations;
        }
        if (configCombinations[key] === undefined) {
          configCombinations[key] = [value];
        } else if (!R.contains(value, configCombinations[key])) {
          configCombinations[key].push(value);
          configCombinations[key].sort();
        }
      });
      return configCombinations;
    }, {});
  };

  getOptions = values => {
    const chartOptions = [];
    chartOptions.push({ value: "*X*", text: "* x-axis *" });
    chartOptions.push({ value: "*COL*", text: "* Column *" });
    chartOptions.push({ value: "*ROW*", text: "* Row *" });
    chartOptions.push({ value: "*COLOR*", text: "* Color *" });

    const valuesOptions = values.map(value => ({
      value: R.toString(value),
      text: typeof value === "string" ? value : R.toString(value),
    }));

    return [...chartOptions, ...valuesOptions];
  };

  getKey = valueToFind => {
    const entry = Object.entries(this.state.config).find(([key, value]) => value === valueToFind);
    return entry ? entry[0] : undefined;
  };

  filterExactValues = sims => {
    // console.log("filter sims, this.state.config", sims, this.state.config);
    return sims.filter(
      sim =>
        !Object.entries(this.state.config).some(([key, value]) => {
          // does it violate constraint ?
          if (value !== "*X*" && value !== "*COL*" && value !== "*ROW*" && value !== "*COLOR*") {
            // if (R.toString(sim.config[key]) !== value) {
            //   console.log("R.toString(sim.config["+ key +"]) !== value", R.toString(sim.config[key]) , value)
            // }
            return R.toString(sim.config[key]) !== value;
          } else {
            return false;
          }
        })
    );
  };

  getSimsBy = (sims, key) => {
    const m = sims.reduce((result, sim) => {
      let sims = result.get(sim.config[key]);
      if (sims === undefined) {
        sims = [];
        result.set(sim.config[key], sims);
      }
      sims.push(sim);
      return result;
    }, new Map());
    return [...m.entries()].sort(([a], [b]) => (a === b ? 0 : a > b ? 1 : -1));
  };

  yFn = sim => sim.profitRelativeDay * 100;

  yFnBoxPlot = sim => {
    const calcY = (dayBegin, days) => {
      const cumulProfitBegin = dayBegin >= 0 ? sim.cumulativeDayStats[dayBegin].profit : 0;
      const cumulProfitEnd = sim.cumulativeDayStats[dayBegin + days].profit;
      const profit = cumulProfitEnd - cumulProfitBegin;
      const profitRelative = profit / (sim.config.tradeTotal + cumulProfitBegin);
      const profitRelativeDay = Math.pow(1 + profitRelative, 1 / days) - 1;
      return profitRelativeDay * 100;
    };

    const y = [];
    const days = this.state.plotChartDays;
    const step = Math.min(1, Math.floor(days / 2));

    for (let dayBegin = -1; dayBegin + days < sim.cumulativeDayStats.length; dayBegin += step) {
      y.push(calcY(dayBegin, days));
    }
    // do the same from end to beginning (so that end values are not cut)
    for (let dayBegin = sim.cumulativeDayStats.length - 1 - days; dayBegin >= -1; dayBegin -= step) {
      y.push(calcY(dayBegin, days));
    }

    // console.log("yFn sim,s y", sim, y);
    return y;
  };

  getChart = (sims, domain, xAxisKey, colorKey) => {
    // console.log("sims, domain, xAxisKey, colorKey", sims, domain, xAxisKey, colorKey);
    const width = 400;
    const height = 400;

    const xFn = sim => sim.config[xAxisKey];

    const simsByColorValue = this.getSimsBy(sims, colorKey);

    const { selSim } = this.state;
    const selSimId = selSim && selSim.id;

    // console.log("simsByColorValue", simsByColorValue);

    return (
      <div style={{ width, height }}>
        <VictoryChart
          width={width}
          height={height}
          domain={domain}
          domainPadding={10}
          theme={VictoryTheme.material}
          key={xAxisKey}
        >
          <VictoryAxis
            dependentAxis
            tickValues={[1]}
            tickCount={1}
            crossAxis={true}
            style={{
              tickLabels: { opacity: 0 },
              axis: { opacity: 0 },
              grid: {
                stroke: "green",
                strokeOpacity: 0.15,
                strokeWidth: 11,
                strokeDasharray: "0",
                strokeLinecap: "butt",
              },
            }}
          />

          <VictoryAxis
            dependentAxis
            tickValues={[0]}
            tickCount={1}
            crossAxis={true}
            style={{
              tickLabels: { opacity: 0 },
              axis: { opacity: 0 },
              grid: {
                stroke: "red",
                strokeOpacity: 0.15,
                strokeWidth: 11,
                strokeDasharray: "0",
                strokeLinecap: "butt",
              },
            }}
          />

          {!colorKey &&
            this.state.plotChartDays > 0 && (
              <VictoryBoxPlot
                data={sims}
                x={xFn}
                y={this.yFnBoxPlot}
                boxWidth={15}
                style={{
                  min: { stroke: "tomato", strokeWidth: 4 },
                  max: { stroke: "orange", strokeWidth: 4 },
                  q1: { fill: "tomato" },
                  q3: { fill: "orange" },
                  median: { stroke: "black", strokeWidth: 4 },
                }}
              />
            )}

          <VictoryGroup colorScale="qualitative">
            {simsByColorValue.map(([key, sims]) => (
              <VictoryLine
                key={key || ""}
                data={sims}
                x={xFn}
                y={this.yFn}
                style={{
                  data: {
                    strokeOpacity: 0.5,
                  },
                }}
              />
            ))}
          </VictoryGroup>
          <VictoryGroup colorScale="qualitative">
            {simsByColorValue.map(([key, sims]) => (
              <VictoryScatter
                key={key || ""}
                data={sims}
                x={xFn}
                y={this.yFn}
                size={d => (d.id === selSimId ? 7 : 4)}
                labels={sim => (colorKey ? "[" + key + "]" : sim.closedTradesCount)}
                style={{
                  data: {
                    stroke: d => (d.id === selSimId ? "#0c0" : null),
                    strokeWidth: 4,
                    cursor: "pointer",
                  },
                  labels: { fontSize: colorKey ? 9 : 10 },
                }}
                events={[
                  {
                    target: "data",
                    eventHandlers: {
                      onClick: () => [
                        {
                          target: "data",
                          mutation: props => {
                            // console.log("data.onClick", props);
                            this.setState({ selSim: props.datum });
                            return undefined;
                          },
                        },
                      ],
                    },
                  },
                ]}
              />
            ))}
          </VictoryGroup>

          <VictoryAxis
            label={xAxisKey}
            tickValues={sims.map(xFn)}
            style={{
              axisLabel: { padding: 28 },
              grid: { stroke: "black", strokeOpacity: 0.2 },
            }}
          />
          <VictoryAxis
            dependentAxis
            crossAxis={false}
            label={"profit per day (%)"}
            style={{
              axisLabel: { padding: 38 },
              grid: { stroke: "black", strokeOpacity: 0.2 },
            }}
          />
        </VictoryChart>
      </div>
    );
  };

  render() {
    const { sims } = this.props;
    const { plotChartDays, selSim } = this.state;

    const configCombinations = this.getConfigCombinations();
    // console.log("configCombinations", configCombinations);
    console.log("stateConfig", this.state.config);
    const notChosen = Object.entries(configCombinations).reduce((notChosen, [key, values]) => {
      if (values.length > 1 && this.state.config[key] === undefined) {
        return notChosen;
      } else {
        return notChosen - 1;
      }
    }, Object.entries(configCombinations).length);

    console.log("notChosen", notChosen);

    const xAxisKey = this.getKey("*X*");
    const colKey = this.getKey("*COL*");
    const rowKey = this.getKey("*ROW*");
    const colorKey = this.getKey("*COLOR*");

    const reduceMin = arr => arr.reduce((a, b) => Math.min(a, b), 0);
    const reduceMax = arr => arr.reduce((a, b) => Math.max(a, b), 1);

    const minY =
      colorKey || !plotChartDays ? reduceMin(sims.map(this.yFn)) : reduceMin(sims.map(this.yFnBoxPlot).map(reduceMin));
    const maxY =
      colorKey || !plotChartDays ? reduceMax(sims.map(this.yFn)) : reduceMax(sims.map(this.yFnBoxPlot).map(reduceMax));

    const domain = { y: [minY, maxY] };

    return (
      <div>
        <div
          style={{
            marginTop: 10,
            marginBottom: 10,
          }}
        >
          <span>PlotChart days ({sims[0].cumulativeDayStats.length} total): </span>
          <select
            value={plotChartDays}
            onChange={e => this.setState({ plotChartDays: Number(e.target.value) })}
            disabled={colorKey}
          >
            {CUMULATIVE_DAYS_VALUES.filter(v => (!colorKey || v === 0) && v < sims[0].cumulativeDayStats.length).map(
              v => (
                <option key={v} value={v}>
                  {v || "hide"}
                </option>
              )
            )}
          </select>
        </div>
        {Object.entries(configCombinations).map(([key, values]) => {
          return values.length > 1 ? (
            <div key={key} style={{ fontWeight: values.length > 1 ? "bold" : "normal" }}>
              {key} ({values.length}):{" "}
              <Dropdown
                key={key}
                placeholder="Select..."
                selection
                options={this.getOptions(values)}
                defaultValue={values.length === 1 ? R.toString(values[0]) : undefined}
                value={this.state.config[key]}
                disabled={values.length === 1}
                onChange={(e, { value }) => {
                  let newConfig = { ...this.state.config, [key]: value };
                  if (value === "*X*" || value === "*COL*" || value === "*ROW*" || value === "*COLOR*") {
                    const currentKey = this.getKey(value);
                    delete newConfig[currentKey];
                  }
                  this.setState({ config: newConfig });
                }}
                error={values.length > 1 && this.state.config[key] === undefined}
              />
            </div>
          ) : null;
        })}

        {!notChosen && xAxisKey ? (
          <div style={{ margin: 20, whiteSpace: "nowrap", overflow: "auto" }}>
            {this.getSimsBy(sims, rowKey).map(([rowValue, sims]) => (
              <div key={rowValue || ""} style={{ margin: 10 }}>
                {this.getSimsBy(sims, colKey).map(([colValue, sims]) => (
                  <div
                    key={colValue || ""}
                    style={{
                      display: "inline-block",
                      textAlign: "center",
                      margin: 10,
                      padding: 10,
                      border: "solid 1px #666",
                    }}
                  >
                    {rowKey && (
                      <h3 style={{ margin: 5 }}>
                        {rowKey}={rowValue || "null"}
                      </h3>
                    )}
                    {colKey && (
                      <h3 style={{ margin: 5 }}>
                        {colKey}={colValue || "null"}
                      </h3>
                    )}
                    {this.getChart(this.filterExactValues(sims), domain, xAxisKey, colorKey)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <Message warning>
            <Icon name="warning" />
            {!xAxisKey ? "Please choose x-axis." : "Please choose all values."}
          </Message>
        )}

        {selSim && <Sim sim={selSim} />}
      </div>
    );
  }
}

export default SimsCharts;



// WEBPACK FOOTER //
// ./src/sim/SimsCharts.js